import { BuildingMenuType, WPFMessageType } from '@/enums';
import type {
    BuildingCategory,
    BuildingInStoreInfo,
    BuildingMenu,
    ContentsPropertyManagement,
    KioskConfigDto,
    PoiContent,
    ScreenSaverInfo,
    StoreInfo,
} from '@/services/dto';
import { createKioskClient } from '@/services/KioskClient';
import { getKioskConfig } from '@/services/operations';
import { S3CacheService } from '@/services/s3CacheService';
import type { ConfigStore, DbResponse, languageType, SetStateFunction, SystemConfig } from '@/stores';
import type { CacheScreenSaverInfo, GNBItemType } from '@/types';
import { dbManager, networkMonitor, ScreenSaverManager } from '@/utils';
import { AppUtils } from '@/utils/app';
import configStoreUtils from '@/utils/configStore';
import { getCategoryCodesFromItem } from '@/utils/poi';
import { isCustomContentItemVisibleForLanguage } from '@/utils/custom-menu/content';
import { enrichPoiWithManagedContents } from '@/utils/poi/enrichPoiContent';
import { formatScreenSaver } from '@/utils/screen-saver';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { SHOP_CATEGORY_CLIENT_CODE_SET } from '@/constants/shop';
import { isDutyFreePoi, isValidLottePoi } from '@/utils/shop';
import { STORE_CODE_MAP_JAMSIL } from '@/constants/app';

export const CONFIG_CONSTANTS = {
    DEFAULT_VOLUME: 3,
    DEFAULT_IDLE_TIMEOUT: 180,
    DEFAULT_LANGUAGE: 'ko',
    DEFAULT_OPEN_TIME: '08:00',
    DEFAULT_CLOSE_TIME: '22:00',
    DEFAULT_SHUTDOWN_TIME: '22:15:00',
    STEP_DELAY: 1000,
    STEP_FINAL_DELAY: 2000,
    LOADING_DELAY: 3000,
} as const;

const resolveLocalizedName = (name: unknown, lang: string): string => {
    if (!name) return '';
    if (typeof name === 'string') return name;
    if (typeof name === 'object') {
        const localized = name as Record<string, string>;
        return localized[lang] || localized['ko'] || '';
    }
    return '';
};

/**
 * Initialize socket client with machine code
 */
const initializeSocketClient = async (kioskCode: string): Promise<void> => {
    try {
        const serverUrl = import.meta.env.VITE_API_INTERNAL_URL;

        if (!kioskCode) {
            return;
        }

        const client = await createKioskClient(serverUrl, kioskCode);
        await client.connect();
    } catch (error) {
        console.error('Failed to initialize socket client:', error);
    }
};

/**
 * Default system configuration
 */
export const defaultConfig: SystemConfig = {
    app: {
        name: 'Lotte Barrier Free Kiosk App',
        version: '1.0.0',
        debug: true,
    },
    server: null,
    ui: {
        contrast: false,
        screenMode: 'fullscreen',
        zoomIn: false,
        zoomClosed: true,
        zoomLevel: 1,
        volume: CONFIG_CONSTANTS.DEFAULT_VOLUME,
        language: CONFIG_CONSTANTS.DEFAULT_LANGUAGE,
        languageOptions: [],
        menu: [],
        logo: null,
        idleTimeoutSec: CONFIG_CONSTANTS.DEFAULT_IDLE_TIMEOUT,
        barrierFree: false,
        screenSaver: [],
        openTime: CONFIG_CONSTANTS.DEFAULT_OPEN_TIME,
        closeTime: CONFIG_CONSTANTS.DEFAULT_CLOSE_TIME,
        mainLanguage: CONFIG_CONSTANTS.DEFAULT_LANGUAGE,
        requestStaffHelp: {
            status: false,
            desc: '',
        },
    },
    kiosk: {
        code: null,
        shutdownTime: CONFIG_CONSTANTS.DEFAULT_SHUTDOWN_TIME,
    },
    tts: {
        voice: false,
        speaker: 'external',
    },
};

/**
 * Loading step management
 */
let timeoutId: ReturnType<typeof setTimeout> | null = null;
let currentStep = 0;

const loadingSteps = [
    { step: 1, delay: CONFIG_CONSTANTS.STEP_DELAY },
    { step: 2, delay: CONFIG_CONSTANTS.STEP_DELAY },
    { step: 3, delay: CONFIG_CONSTANTS.STEP_DELAY },
    { step: 4, delay: CONFIG_CONSTANTS.STEP_FINAL_DELAY },
];

/**
 * Run loading steps sequentially
 */
export function runLoadingSteps(setState: SetStateFunction, startStep?: number, finalCallback?: () => void): void {
    stopLoadingSteps();

    const startIndex = startStep ? loadingSteps.findIndex((s) => s.step === startStep) : 0;
    const validStartIndex = startIndex >= 0 ? startIndex : 0;

    currentStep = startStep || loadingSteps[0]?.step || 1;

    if (startStep) {
        setState({ step: startStep });
    }

    runNextStep(validStartIndex, setState, finalCallback);
}

/**
 * Run next step in sequence
 */
function runNextStep(index: number, setState: SetStateFunction, finalCallback?: () => void): void {
    if (index >= loadingSteps.length) {
        if (finalCallback) finalCallback();
        return;
    }

    timeoutId = setTimeout(() => {
        currentStep = loadingSteps[index].step;
        setState({ step: currentStep });

        if (index === loadingSteps.length - 1) {
            setState({ isLoading: false });
        }

        runNextStep(index + 1, setState, finalCallback);
    }, loadingSteps[index].delay);
}

/**
 * Stop loading steps
 */
export function stopLoadingSteps(): void {
    if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
    }
}

/**
 * Run Loading from specific Step
 */
export function runFromStep(setState: SetStateFunction, step: number, finalCallback?: () => void): void {
    runLoadingSteps(setState, step, finalCallback);
}

/**
 * Get current loading step
 */
export function getCurrentStep(): number {
    return currentStep;
}

/**
 * Cleanup function for config store
 */
export const cleanupConfigStore = (): void => {
    stopLoadingSteps();
};

/**
 * Helper function to build menu items from API configuration
 */
export const buildMenuFromApiConfig = (apiConfig: KioskConfigDto, language: string): GNBItemType[] => {
    if (!apiConfig.buildingMenus?.[language]) {
        return [];
    }

    return apiConfig.buildingMenus[language].map((item: BuildingMenu) => ({
        id: item.id,
        type: item.menuType,
        label: item.name || '',
        icon: item.iconUrl || null,
        disabled: !item.isActive,
        ariaLabel: item.name || null,
        dataDesc: item.description || null,
        textColor: item.textColor || null,
        imageUrl: item.imageUrl || null,
        customMenuType: item.customMenuType || null,
        menuCode: item.menuCode || null,
    }));
};

/**
 * Helper function to build menu items from buildingMenus and building info
 * Used when we have separate buildingMenus and building data instead of full KioskConfigDto
 */
export const buildMenuFromBuildingMenus = (
    buildingMenus: Record<string, BuildingMenu[]> | null | undefined,
    language: string,
    mainLanguage: string
): GNBItemType[] => {
    if (!buildingMenus?.[language]) {
        return [];
    }

    const mainMenus = buildingMenus[mainLanguage] || [];
    const mainLanguageMap = new Map(mainMenus.map((item) => [item.id, item]));

    const getLabelString = (name: any): string => {
        if (!name) return '';
        if (typeof name === 'string') return name;
        return name?.line1 || '';
    };

    return buildingMenus[language].map((item: BuildingMenu) => {
        let label = getLabelString(item.name);

        if (!label) {
            const fallbackItem = mainLanguageMap.get(item.id);
            label = getLabelString(fallbackItem?.name);
        }

        return {
            id: item.id,
            type: item.menuType,
            label: label,
            icon: item.iconUrl || null,
            disabled: !item.isActive,
            ariaLabel: label || null,
            dataDesc: item.description || null,
            textColor: item.textColor || null,
            imageUrl: item.imageUrl || null,
            customMenuType: item.customMenuType || null,
            menuCode: item.menuCode || null,
        };
    });
};

export const getRequestStaffHelpFromBuildingMenus = (
    buildingMenus: Record<string, BuildingMenu[]> | null | undefined,
    language: string
): { status: boolean; desc: string } => {
    const home = buildingMenus?.[language]?.find((m) => m.menuType === BuildingMenuType.HOME);
    return {
        status: home?.staffAssistanceEnabled === true,
        desc: (home?.staffAssistanceText ?? '').trim(),
    };
};
/**
 * Resolves global menu categories to building-specific category codes.
 * Category names come from building.buildingCategories (LocalizedString) so UI language can switch correctly.
 * Global menu selectedCategories only carry a single-language string from mainLanguage (ko).
 */
const resolveCategoriesForBuilding = (building: BuildingInStoreInfo, globalCategories: BuildingCategory[]): BuildingCategory[] => {
    const codesInBuilding = new Set(building.buildingCategories.map((category) => category.categoryCode));

    return globalCategories
        .map((category) => {
            const mergeCategoryCode = getCategoryCodesFromItem(category).filter((code) => codesInBuilding.has(code));
            if (!mergeCategoryCode.length) return null;

            const buildingCategory = building.buildingCategories.find((item) => item.clientCode === category.clientCode);

            return {
                ...category,
                id: buildingCategory?.id ?? category.id,
                name: buildingCategory?.name ?? category.name,
                mergeCategoryCode,
            };
        })
        .filter((category): category is BuildingCategory & { mergeCategoryCode: string[] } => category !== null);
};

/**
 * Helper function to process store configuration
 */
const processStoreConfig = (
    storeConfig: StoreInfo | null,
    buildingCategoriesSelected?: BuildingCategory[],
    facilityCategoriesSelected?: BuildingCategory[]
): StoreInfo | null => {
    if (!storeConfig) return null;

    const clonedStore = { ...storeConfig };
    if (clonedStore.buildings) {
        const storeLevelPropertyManagement = clonedStore.contentsPropertyManagement;
        clonedStore.buildings = configStoreUtils.processBuildings(
            clonedStore.buildings.map((building) => ({
                ...building,
                contentsPropertyManagement: building.contentsPropertyManagement ?? storeLevelPropertyManagement ?? [],
                buildingCategoriesSelected: buildingCategoriesSelected,
                facilityCategoriesSelected: resolveCategoriesForBuilding(building, facilityCategoriesSelected ?? []),
            })) as BuildingInStoreInfo[]
        );
    }

    return clonedStore;
};

/**
 * Helper function to process screen saver items
 */
const processScreenSaverItems = async (screenSaverData: ScreenSaverInfo[] | undefined, set: SetStateFunction): Promise<CacheScreenSaverInfo[]> => {
    if (screenSaverData && Array.isArray(screenSaverData) && screenSaverData.length > 0) {
        runLoadingSteps(set, 2);

        try {
            const processedItems = await ScreenSaverManager.processScreenSaverItems(screenSaverData as ScreenSaverInfo[]);
            return processedItems;
        } catch {
            return [];
        }
    }
    return [];
};

/**
 * Helper function to create merged configuration object
 */
const createMergedConfig = (
    apiConfig: KioskConfigDto,
    cachedScreenSaverItems: CacheScreenSaverInfo[],
    serverConfig: SystemConfig['server'],
    processedStore: StoreInfo | null
): SystemConfig => {
    const currentLanguage = (apiConfig.store?.storeLangs?.find((lang) => lang.main_lang)?.code as languageType) || CONFIG_CONSTANTS.DEFAULT_LANGUAGE;
    return {
        app: { ...defaultConfig.app },
        ui: {
            ...defaultConfig.ui,
            menu: buildMenuFromApiConfig(apiConfig, currentLanguage),
            logo: apiConfig.building?.logoUrl || null,
            idleTimeoutSec: apiConfig.building?.idleTimeoutSec || CONFIG_CONSTANTS.DEFAULT_IDLE_TIMEOUT,
            barrierFree: apiConfig.machine?.barrierFree || false,
            language: currentLanguage,
            languageOptions: apiConfig.store?.storeLangs ? AppUtils.OPTION_LANGUAGE(apiConfig.store?.storeLangs || []) : [],
            screenSaver: cachedScreenSaverItems || [],
            openTime: apiConfig.building?.openTime || '',
            closeTime: apiConfig.building?.closeTime || '',
            mainLanguage: currentLanguage,
            requestStaffHelp: getRequestStaffHelpFromBuildingMenus(apiConfig.buildingMenus, currentLanguage),
        },
        tts: {
            ...defaultConfig.tts,
        },
        kiosk: {
            code: apiConfig.machine?.code || null,
            shutdownTime: apiConfig.building?.shutdownTime || CONFIG_CONSTANTS.DEFAULT_SHUTDOWN_TIME,
        },
        server: serverConfig,
    };
};

/**
 * Create config store with Zustand
 */
export const useConfigStore = create<ConfigStore>()(
    devtools(
        (set, get) => ({
            config: defaultConfig,
            isLoading: true,
            step: 0,
            isOnline: networkMonitor.getStatus(),

            loadConfigFromStorage: async (): Promise<SystemConfig> => {
                try {
                    const cachedConfig = await dbManager.get<DbResponse>('config', 'systemConfig');
                    return (cachedConfig?.value as SystemConfig) || defaultConfig;
                } catch {
                    return defaultConfig;
                }
            },

            initConfig: async () => {
                const kioskCode = localStorage.getItem('kioskCode');
                cleanupConfigStore();
                set({ isLoading: true });

                try {
                    // Set up network status monitoring
                    networkMonitor.addListener(async (isOnline: boolean) => {
                        get().setOnlineStatus(isOnline);
                        if (kioskCode) {
                            await initializeSocketClient(kioskCode);
                        }
                    });

                    // Attempt to fetch fresh config if online
                    if (networkMonitor.getStatus()) {
                        await get().fetchConfig();
                    }
                } catch {
                    set({ config: defaultConfig });
                }
            },

            fetchConfig: async () => {
                await dbManager.init();
                const loadedConfig = await get().loadConfigFromStorage();
                const kioskCode = localStorage.getItem('kioskCode');
                const state = get();

                const shouldSkipFetch = !networkMonitor.shouldFetchToday() || !state.isOnline || !kioskCode;
                if (shouldSkipFetch) {
                    set({ config: loadedConfig });
                    runFromStep(set, 1);

                    if (kioskCode) {
                        await initializeSocketClient(kioskCode);
                    }
                    return;
                }

                try {
                    runLoadingSteps(set, 1);
                    const response = await getKioskConfig({ machineCode: kioskCode });

                    const apiConfigData = response?.data ?? ({} as KioskConfigDto);
                    const processedData = await S3CacheService.getProcessedStoreConfig(apiConfigData);
                    const { screenSaver: rawScreenSaver, ...restApiConfig } = processedData;

                    const apiConfig = {
                        ...restApiConfig,
                        screenSaver: formatScreenSaver(rawScreenSaver),
                    };

                    if (!response.ok || !apiConfig) {
                        throw new Error('Failed to fetch kiosk config');
                    }

                    const mainLanguage =
                        (apiConfig.store?.storeLangs?.find((lang: { main_lang: boolean }) => lang.main_lang)?.code as string) ||
                        CONFIG_CONSTANTS.DEFAULT_LANGUAGE;

                    // Process store configuration
                    const menuBuildingData = apiConfig.buildingMenus;

                    const contentsPropertyManagement: ContentsPropertyManagement[] = apiConfig.store?.contentsPropertyManagement ?? [];

                    if (menuBuildingData) {
                        const currentStoreBuilding = (apiConfig.store?.buildings ?? []).find(
                            (building: { id: string }) => building.id === apiConfig.building?.id
                        );
                        const currentBuildingCategoriesByClientCode = new Map<string, BuildingCategory>(
                            (currentStoreBuilding?.buildingCategories ?? []).map((cat: BuildingCategory) => [cat.clientCode, cat])
                        );

                        const enrichedBuildings: { id: string; studioBuildingId: string; poiContents: PoiContent[] }[] = (apiConfig.store?.buildings ?? []).map(
                            (building: any) => {
                                return {
                                    id: building.id,
                                    studioBuildingId: building.studioBuildingId,
                                    poiContents: (building.poiContents ?? []).map((poi: PoiContent) =>
                                        enrichPoiWithManagedContents(poi, contentsPropertyManagement, building.buildingCategories)
                                    ),
                                    buildingMenus: building.buildingMenus ?? [],
                                };
                            }
                        );

                        const allPoiContents: PoiContent[] = enrichedBuildings.flatMap((b) => b.poiContents);
                        const buildingMenus = (apiConfig.store?.buildings ?? []).map((building: any) => building.buildingMenus ?? []);

                        const customContentByMenuId = new Map<string, any[]>();
                        const customMenuCategoryByMenuId = new Map<string, any[]>();
                        const customMenuTypeByMenuId = new Map<string, string>();
                        buildingMenus.flat().forEach((bMenu: any) => {
                            if (bMenu.id && bMenu.customMenu?.customMenuContents?.length) {
                                customContentByMenuId.set(bMenu.id, bMenu.customMenu.customMenuContents);
                                customMenuCategoryByMenuId.set(bMenu.id, bMenu.customMenu.custom_menu_content_category);
                            }

                            if (bMenu.id) {
                                const customMenuType = bMenu.customMenuType ?? bMenu.custom_menu_type ?? bMenu.customMenu?.menu_type;
                                if (customMenuType != null && customMenuType !== '') {
                                    customMenuTypeByMenuId.set(bMenu.id, String(customMenuType));
                                }
                            }
                        });

                        Object.keys(menuBuildingData).forEach((lang) => {
                            menuBuildingData[lang]?.forEach((menu: any) => {
                                if (menu.selectedCategories?.length) {
                                    const groupedByClientCode = new Map<string, any>();

                                    menu.listCategory = menu.selectedCategories;

                                    const categoriesIndentLevel2 = menu.selectedCategories.filter((cat: any) => cat.indentLevel === 2);

                                    categoriesIndentLevel2.forEach((cat: any) => {
                                        const existing = groupedByClientCode.get(cat.clientCode);
                                        if (existing) {
                                            existing.mergeCategoryCode.push(cat.categoryCode);
                                        } else {
                                            groupedByClientCode.set(cat.clientCode, {
                                                ...cat,
                                                mergeCategoryCode: [cat.categoryCode],
                                            });
                                        }
                                    });

                                    menu.selectedCategories = Array.from(groupedByClientCode.values()).map((cat) => {
                                        const buildingCategory = currentBuildingCategoriesByClientCode.get(cat.clientCode);
                                        const name = resolveLocalizedName(cat.name, lang);

                                        if (!buildingCategory) {
                                            return { ...cat, name };
                                        }

                                        return {
                                            ...cat,
                                            id: buildingCategory.id,
                                            categoryCode: buildingCategory.categoryCode,
                                            name: name || resolveLocalizedName(buildingCategory.name, lang),
                                        };
                                    });

                                    const allCategoryCodes = menu.selectedCategories.flatMap((cat: any) => cat.mergeCategoryCode ?? []);
                                    const categoryCodesSet = new Set<string>(allCategoryCodes);

                                    const storeCode = apiConfig.store?.storeCode ?? '';
                                    const isBranchStore = storeCode === STORE_CODE_MAP_JAMSIL;
                                    const isValidPoiForStore = (poi: PoiContent) => {
                                        if (!isBranchStore) return true;
                                        if (isDutyFreePoi(poi)) return true;
                                        return isValidLottePoi(poi);
                                    };

                                    menu.poiContents = allPoiContents.filter((poi: any) => {
                                        if (!isValidPoiForStore(poi)) return false;
                                        const poiCategories: string[] = poi.categories ?? poi.rootCategories ?? [];
                                        return poiCategories.some((code: string) => categoryCodesSet.has(code));
                                    });

                                    menu.poiContentBuildings =
                                        enrichedBuildings.map((building) => ({
                                            id: building.id,
                                            studioBuildingId: building.studioBuildingId,
                                            poiContents: (building.poiContents ?? []).filter((poi: any) => {
                                                if (!isValidPoiForStore(poi)) return false;
                                                const poiCategories: string[] = poi.categories ?? poi.rootCategories ?? [];
                                                return poiCategories.some((code: string) => categoryCodesSet.has(code));
                                            }),
                                        })) ?? [];

                                    const hasMatchingPoi = (cat: any) => {
                                        const codes: string[] = cat.mergeCategoryCode ?? [cat.categoryCode];
                                        return menu.poiContents.some((poi: any) => {
                                            const poiCategories: string[] = poi.categories ?? poi.rootCategories ?? [];
                                            return codes.some((code: string) => poiCategories.includes(code));
                                        });
                                    };

                                    // SEARCH: chỉ giữ clientCode trong bảng duty-free + còn POI
                                    if (menu.menuType === BuildingMenuType.SEARCH) {
                                        menu.selectedCategories = menu.selectedCategories.filter(
                                            (cat: any) => SHOP_CATEGORY_CLIENT_CODE_SET.has(cat.clientCode) && hasMatchingPoi(cat)
                                        );
                                    } else {
                                        menu.selectedCategories = menu.selectedCategories.filter(hasMatchingPoi);
                                    }
                                }

                                const rawCustomContents = customContentByMenuId.get(menu.id) ?? [];
                                const now = new Date();
                                now.setHours(0, 0, 0, 0);

                                const customMenuType = menu.customMenuType ?? customMenuTypeByMenuId.get(menu.id) ?? menu.customMenu?.menu_type ?? null;
                                menu.customMenuType = customMenuType;

                                menu.customContent = rawCustomContents
                                    .filter((item: any) => {
                                        if (!isCustomContentItemVisibleForLanguage(item, lang, customMenuType)) {
                                            return false;
                                        }

                                        let isValidBegin = true;
                                        let isValidEnd = true;

                                        if (item.eventDateBegin) {
                                            const beginDate = new Date(item.eventDateBegin.replace(/-/g, '/'));
                                            if (!isNaN(beginDate.getTime())) {
                                                isValidBegin = beginDate.getTime() <= now.getTime();
                                            }
                                        }

                                        if (item.eventDateEnd) {
                                            const endDate = new Date(item.eventDateEnd.replace(/-/g, '/'));
                                            if (!isNaN(endDate.getTime())) {
                                                endDate.setHours(23, 59, 59, 999);
                                                isValidEnd = endDate.getTime() >= now.getTime();
                                            }
                                        }

                                        return isValidBegin && isValidEnd;
                                    })
                                    .map((item: any) => ({
                                        ...item,
                                        title: item.title?.[lang] || item.title?.['ko'] || '',
                                        contents: item.contents?.[lang] || item.contents?.['ko'] || '',
                                        categoryName: item.categoryName?.[lang] || item.categoryName?.['ko'] || '',
                                    }));

                                const rawCustomMenuCategory = customMenuCategoryByMenuId.get(menu.id)?.filter((item) => item.isConnect) ?? [];
                                menu.customMenuCategory = rawCustomMenuCategory.map((item: any) => ({
                                    ...item,
                                    name: item.name?.[lang] || item.name?.['ko'] || '',
                                }));

                                menu.menuCode = menu.menuCode ?? '';
                            });
                        });
                    }

                    const buildingCategorySelected =
                        apiConfig.buildingMenus?.[mainLanguage]?.find((menu: { menuType: string }) => menu.menuType === BuildingMenuType.SEARCH)
                            ?.selectedCategories ?? [];
                    const facilityCategorySelected =
                        apiConfig.buildingMenus?.[mainLanguage]?.find((menu: { menuCode?: string | null }) => menu.menuCode === BuildingMenuType.FACILITIES)
                            ?.selectedCategories ?? [];

                    const processedStore = processStoreConfig(apiConfig.store, buildingCategorySelected, facilityCategorySelected);

                    if (processedStore) {
                        apiConfig.store = processedStore;
                    }

                    processedStore?.buildings?.forEach((building) => {
                        if (building.poiContents) {
                            building.poiContents = building.poiContents.map((poi) => enrichPoiWithManagedContents(poi, contentsPropertyManagement));
                        }
                    });

                    // Merge server configuration
                    const serverConfig = apiConfig ? { ...defaultConfig.server, ...apiConfig } : defaultConfig.server;

                    // Handle screen saver items
                    const cachedScreenSaverItems = await processScreenSaverItems(apiConfig.screenSaver, set);

                    // Handle shutdown timeout
                    if (window.chrome?.webview) {
                        window.chrome.webview.postMessage({
                            type: WPFMessageType.ShutdownTimeout,
                            time: apiConfig.building?.shutdownTime,
                        });
                    }

                    // Create merged configuration
                    const mergedConfig: SystemConfig = createMergedConfig(apiConfig, cachedScreenSaverItems, serverConfig, processedStore);

                    // Save and update configuration
                    await dbManager.set('config', 'systemConfig', mergedConfig);
                    set({ config: mergedConfig });
                    sessionStorage.setItem('lastConfigFetch', new Date().toDateString());

                    // Initialize socket client
                    await initializeSocketClient(apiConfig.machine?.code || kioskCode);
                } catch (error) {
                    console.error('Failed to fetch config, loading from storage', error);
                    set({ config: loadedConfig });
                } finally {
                    runLoadingSteps(set, 3);
                }
            },

            updateConfig: async (newConfig) => {
                const currentConfig = get().config;

                const updatedConfig: SystemConfig = {
                    app: { ...currentConfig.app, ...newConfig.app },
                    ui: { ...currentConfig.ui, ...newConfig.ui },
                    kiosk: { ...currentConfig.kiosk, ...newConfig.kiosk },
                    server: newConfig.server ? { ...currentConfig.server, ...newConfig.server } : currentConfig.server,
                    tts: { ...currentConfig.tts, ...newConfig.tts },
                };
                await dbManager.set('config', 'systemConfig', updatedConfig);
                set({ config: updatedConfig });
            },

            resetConfig: () => {
                cleanupConfigStore();

                set({ config: defaultConfig });

                dbManager.delete('config', 'systemConfig');
                sessionStorage.removeItem('lastConfigFetch');
            },

            setOnlineStatus: (isOnline) => {
                const currentState = get();
                set({ isOnline });

                if (!currentState.isOnline && isOnline && networkMonitor.shouldFetchToday()) {
                    get().fetchConfig();
                }
            },

            setLoading: (isLoading) => {
                set({ isLoading });
            },

            setStep: (step) => {
                set({ step });
            },
        }),
        {
            name: 'config-store',
        }
    )
);
